import {
    Button,
    Card,
    CardBody,
    Flex,
    Icon,
    Table,
    TableContainer,
    Tbody,
    Th,
    Thead,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { BiPlus } from 'react-icons/bi';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { Asset, PortfolioBase } from '../_openapi/portfolio-optimizer';
import { SIdQuote } from '../_openapi/pricing';
import { AssetDrawer, useAssetDrawerControls } from '../Asset/AssetDrawer/AssetDrawer';
import { PageHeader } from '../widgets/PageHeader';
import { CSVUpload } from './CSVUpload';
import { ManualTradeReviewModal } from './ManualTradeReviewModal';
import { useTradeBlotterContext } from './TradeBlotterContext';
import { TradeLineItem } from './TradeLineItem';

export type PriceType = 'MANUAL' | 'ASK' | 'BID' | 'MID';
export type ManualTradePrice = {
    type: PriceType;
    percentage?: number;
    value?: number;
};
export interface ManualTrade {
    portfolio?: PortfolioBase;
    asset?: Asset;

    side?: string;
    quantity?: number;
    orderType?: 'MKT' | 'LMT';
    quote?: SIdQuote;
    price?: ManualTradePrice;
    budget?: number;
}

export const TradeBlotter = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { trades, addTrade, addTrades } = useTradeBlotterContext();
    const [reviewData, setReviewData] = useState<ManualTrade[]>([]);

    const isInvalid = useMemo(() => {
        if (!trades.length) {
            return true;
        }

        return trades.some(
            (trade) => !trade.asset || !trade.portfolio || !trade.quote || !trade.quantity || !trade.side,
        );
    }, [trades]);

    return (
        <>
            <PageHeader
                title="Trade Blotter"
                info="Click Account Alias for portfolio details, click security description for security details, click trade value to calculate trade from a specified budget "
            >
                <Flex gap={2}>
                    <Button
                        alignSelf="flex-start"
                        colorScheme="teal"
                        leftIcon={<Icon as={BiPlus} />}
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            addTrade({
                                orderType: 'LMT',
                                quantity: 0,
                            })
                        }
                    >
                        Add new trade
                    </Button>
                    <CSVUpload addTrades={addTrades} />
                    <Button
                        isDisabled={isInvalid}
                        colorScheme="teal"
                        size="sm"
                        onClick={() => {
                            setReviewData(trades);
                            onOpen();
                        }}
                    >
                        Review before submit
                    </Button>
                </Flex>
            </PageHeader>
            <Card>
                <CardBody>
                    <Trades />
                </CardBody>
            </Card>
            <ManualTradeReviewModal isOpen={isOpen} onClose={onClose} trades={reviewData} />
        </>
    );
};

const HEADERS = ['Portfolio', 'Side', 'Security', 'Price', 'Order Type', 'Quantity', ''];
const Trades = () => {
    const { trades } = useTradeBlotterContext();
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();
    const { viewAsset, ...assetDrawerProps } = useAssetDrawerControls();

    return (
        <>
            <TableContainer>
                <Table size="sm">
                    <Thead>
                        <Tr>
                            {HEADERS.map((label) => (
                                <Th key={label} px={2} py={1} borderColor="gray.600">
                                    {label}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {trades.map((_, idx) => (
                            <TradeLineItem key={idx} index={idx} viewPortfolio={viewPortfolio} viewAsset={viewAsset} />
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
            <PortfolioDrawer {...portfolioDrawerProps} />
            <AssetDrawer {...assetDrawerProps} />
        </>
    );
};
